// Auth form (login/register) and connection status pills.
import { useEffect, useState, type FormEvent } from 'react';
import {
  login,
  register,
  sendRegisterCode,
  confirmRegisterCode,
  type AuthResponse,
} from '../api';
import { APP_NAME } from '../branding';
import { PWA_BUILD_ID } from '../buildInfo';

interface AuthGateProps {
  onSubmit: (creds: AuthResponse) => void;
}

const ENC_WARNINGS = [
  'The encryption password protects files you upload as .hcloud.',
  'It is never sent to the server in clear text - only a verifier hash is stored.',
  'If you forget it, encrypted files cannot be recovered. Store it safely.',
];

export function AuthGate({ onSubmit }: AuthGateProps): JSX.Element {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [encPassword, setEncPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [otp, setOtp] = useState('');
  const [regTicket, setRegTicket] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [codeBusy, setCodeBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showEncPassword, setShowEncPassword] = useState(false);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get('register') === '1' || q.has('register')) setMode('register');
    const em = q.get('email');
    if (em) setEmail(em);
    const urlInvite = q.get('inviteCode');
    if (urlInvite) setInviteCode(urlInvite);
    const urlCode = q.get('code');
    if (urlCode) setOtp(urlCode.replace(/\D/g, '').slice(0, 6));
    const ticket = q.get('regTicket');
    if (ticket) {
      setRegTicket(ticket);
      setEmailVerified(true);
    }
    if (q.get('verified') === '1') setEmailVerified(true);
    if (q.get('verifyError') === '1') {
      setError('That link has expired. Request a new code below.');
    }

    const strip = () => {
      const clean = new URL(window.location.href);
      clean.searchParams.delete('regTicket');
      clean.searchParams.delete('verified');
      clean.searchParams.delete('verifyError');
      clean.searchParams.delete('code');
      clean.searchParams.delete('inviteCode');
      const next = clean.pathname + (clean.search ? clean.search : '');
      window.history.replaceState({}, '', next);
    };

    if (em && urlCode && /^\d{6}$/.test(urlCode.replace(/\D/g, '')) && !ticket) {
      const code = urlCode.replace(/\D/g, '').slice(0, 6);
      setCodeBusy(true);
      void confirmRegisterCode(em, code)
        .then((res) => {
          setRegTicket(res.regTicket);
          setEmailVerified(true);
          setEmail(res.email);
          setCodeSent(true);
          strip();
        })
        .catch((err: unknown) => {
          setError(err instanceof Error ? err.message : 'Could not verify code from link');
        })
        .finally(() => setCodeBusy(false));
      return;
    }

    if (ticket || q.has('verifyError') || q.has('verified')) {
      strip();
    }
  }, []);

  const sendCode = async () => {
    setError(null);
    setCodeBusy(true);
    try {
      await sendRegisterCode(email);
      setCodeSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send code');
    } finally {
      setCodeBusy(false);
    }
  };

  const verifyOtp = async () => {
    setError(null);
    setCodeBusy(true);
    try {
      const res = await confirmRegisterCode(email, otp);
      setRegTicket(res.regTicket);
      setEmailVerified(true);
      setEmail(res.email);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid code');
    } finally {
      setCodeBusy(false);
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (mode === 'register' && !emailVerified && !inviteCode.trim()) {
      setError('Verify your email or enter an access code before creating your account.');
      return;
    }
    setPending(true);
    try {
      const creds =
        mode === 'login'
          ? await login(email, password)
          : await register({
              email,
              password,
              ...(encPassword ? { encPassword } : {}),
              ...(inviteCode.trim() ? { inviteCodes: [inviteCode.trim()] } : {}),
              ...(regTicket ? { regTicket } : {}),
            });
      if (encPassword) {
        try {
          window.sessionStorage.setItem('hc_enc_password', encPassword);
        } catch {
          /* private mode */
        }
      }
      onSubmit(creds);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed');
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="auth-wrap">
      <form className="card auth-card" onSubmit={submit}>
        <div className="auth-brand">
          <span className="auth-brand-mark" aria-hidden>
            <svg width="36" height="36" viewBox="0 0 24 24">
              <path
                fill="#1a73e8"
                d="M12 2L3 7v10l9 5 9-5V7l-9-5zm0 2.2l6.5 3.6L12 11.4 5.5 7.8 12 4.2zM5 9.4l6 3.3v6.6l-6-3.3V9.4zm14 0v6.6l-6 3.3v-6.6l6-3.3z"
              />
            </svg>
          </span>
          <h1 className="auth-brand-title">{APP_NAME}</h1>
        </div>
        <h2 className="auth-heading">{mode === 'login' ? 'Sign in' : 'Create account'}</h2>
        <label>
          Email
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
            readOnly={emailVerified && mode === 'register'}
            placeholder="you@example.com"
          />
        </label>
        {mode === 'register' && (
          <section className="auth-verify" aria-label="Verify email">
            <p className="auth-verify-lead">
              {emailVerified
                ? 'Email verified — choose a password to finish.'
                : 'Verify your email with a one-time code, or use an access code from a folder invite.'}
            </p>
            {!emailVerified && (
              <>
                <label>
                  Access code from invite email
                  <input
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value)}
                    placeholder="e.g. abcd-efgh-ijkl-mnop"
                    autoComplete="off"
                  />
                </label>
                <p className="auth-divider"><span>or</span></p>
                <div className="auth-otp-row">
                  {!codeSent ? (
                    <button
                      type="button"
                      className="btn"
                      disabled={codeBusy || !email.trim()}
                      onClick={() => void sendCode()}
                    >
                      {codeBusy ? 'Sending…' : 'Email me a sign-up code'}
                    </button>
                  ) : (
                    <>
                      <label>
                        6-digit code
                        <input
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          maxLength={6}
                          value={otp}
                          onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="000000"
                        />
                      </label>
                      <button
                        type="button"
                        className="btn primary"
                        disabled={codeBusy || otp.length < 6}
                        onClick={() => void verifyOtp()}
                      >
                        {codeBusy ? 'Checking…' : 'Verify code'}
                      </button>
                      <button
                        type="button"
                        className="btn link"
                        disabled={codeBusy}
                        onClick={() => void sendCode()}
                      >
                        Resend code
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </section>
        )}
        <label>
          Password
          <span className="password-field">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              minLength={mode === 'register' ? 8 : undefined}
              required
              placeholder="••••••••"
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
            >
              {showPassword ? <IconEyeOff /> : <IconEye />}
            </button>
          </span>
        </label>
        {mode === 'register' && (
          <>
            <label>
              Encryption password <span className="opt">(optional)</span>
              <span className="password-field">
                <input
                  type={showEncPassword ? 'text' : 'password'}
                  value={encPassword}
                  onChange={(e) => setEncPassword(e.target.value)}
                  autoComplete="new-password"
                  minLength={10}
                  placeholder="Protects .hcloud uploads"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowEncPassword((v) => !v)}
                  aria-label={showEncPassword ? 'Hide encryption password' : 'Show encryption password'}
                  aria-pressed={showEncPassword}
                >
                  {showEncPassword ? <IconEyeOff /> : <IconEye />}
                </button>
              </span>
            </label>
            {encPassword && (
              <ul className="warnings">
                {ENC_WARNINGS.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            )}
          </>
        )}
        {error && <div className="banner error">{error}</div>}
        <button className="btn primary" type="submit" disabled={pending}>
          {pending ? 'Please wait…' : mode === 'login' ? 'Sign in' : 'Create account'}
        </button>
        <button
          type="button"
          className="btn link"
          onClick={() => {
            setMode(mode === 'login' ? 'register' : 'login');
            setError(null);
          }}
        >
          {mode === 'login' ? 'Need an account? Register' : 'Have an account? Sign in'}
        </button>
      </form>
      <span className="pwa-build-stamp" aria-label={`PWA build ${PWA_BUILD_ID}`}>
        {PWA_BUILD_ID}
      </span>
    </div>
  );
}

function IconEye(): JSX.Element {
  return (
    <svg className="password-toggle-icon" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M12 5c-5 0-9.27 3.11-10 7.5C2.73 16.89 6 20 12 20s9.27-3.11 10-7.5C21.27 8.11 17 5 12 5zm0 12.5A5 5 0 1112 7a5 5 0 010 10.5zm0-2.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z"
      />
    </svg>
  );
}

function IconEyeOff(): JSX.Element {
  return (
    <svg className="password-toggle-icon" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M3.27 2.5 2 3.77l2.09 2.09C2.73 7.11 1.46 9.18 1 11.5 1.73 15.89 6 19 12 19c1.61 0 3.11-.35 4.43-.97L19.23 21l1.27-1.27L3.27 2.5zM12 17c-4 0-7.27-2.11-8-5.5.34-1.52 1.2-2.87 2.36-3.86l1.53 1.53A5.002 5.002 0 0012 16.5c.55 0 1.08-.09 1.57-.26l1.74 1.74A9.9 9.9 0 0112 17zm8.73-5.5c-.46-2.32-1.73-4.39-3.36-5.88l1.46 1.46c1.16.99 2.02 2.34 2.36 3.86-.73 3.39-4 5.5-8 5.5-.87 0-1.7-.13-2.47-.36l1.62 1.62c.49.17 1.02.26 1.57.26 6 0 10.27-3.11 11-7.5z"
      />
    </svg>
  );
}
